import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  Chip
} from '@mui/material';
import dayjs from 'dayjs';

import PerformanceFilterPanel from '../Analytics/PerformanceFilterPanel';
import ScoreDistributionChart from '../Analytics/ScoreDistributionChart';
import TrendLineChart from '../Analytics/TrendLineChart';
import RubricHeatmap from '../Analytics/RubricHeatmap';
import { analyticsAPI } from '../../services/api';

const defaultFilters = {
  startDate: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
  endDate: dayjs().format('YYYY-MM-DD'),
  granularity: 'day',
  caseIds: [],
  groupIds: [],
  originTypes: [],
  authorTypes: []
};

const ORIGIN_LABELS = {
  'virtual-medic': 'Virtual Medic',
  tenant: 'Tenant',
  external: 'External'
};

const AUTHOR_LABELS = {
  'virtual-medic-instructor': 'Virtual Medic instructor',
  'tenant-author': 'Tenant author',
  'external-contributor': 'External contributor',
  system: 'System'
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const StudentPerformanceDashboard = () => {
  const [filters, setFilters] = useState(defaultFilters);
  const [analytics, setAnalytics] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [savedViews, setSavedViews] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedViewId, setSelectedViewId] = useState('');
  const [exportAnchor, setExportAnchor] = useState(null);

  const loadAnalytics = useCallback(async (payload = {}) => {
    setLoading(true);
    try {
      const response = await analyticsAPI.performance({ ...payload });
      setAnalytics(response.data.analytics);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSnapshots = useCallback(async () => {
    try {
      const response = await analyticsAPI.snapshots({ limit: 5 });
      setSnapshots(response.data?.snapshots || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load snapshots', err);
    }
  }, []);

  const loadViews = useCallback(async () => {
    try {
      const response = await analyticsAPI.listViews();
      setSavedViews(response.data?.views || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load saved views', err);
    }
  }, []);

  const loadSubscriptions = useCallback(async () => {
    try {
      const response = await analyticsAPI.listDigests();
      setSubscriptions(response.data?.subscriptions || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load digest subscriptions', err);
    }
  }, []);

  useEffect(() => {
    loadViews();
    loadSnapshots();
    loadSubscriptions();
  }, [loadViews, loadSnapshots, loadSubscriptions]);

  useEffect(() => {
    loadAnalytics({ ...filters, viewId: selectedViewId || undefined });
  }, [filters, loadAnalytics, selectedViewId]);

  const handleFiltersChange = (updated) => {
    setFilters(updated);
  };

  const handleCreateView = async (payload) => {
    try {
      await analyticsAPI.createView(payload);
      await loadViews();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save view');
    }
  };

  const handleSelectView = async (viewId) => {
    setSelectedViewId(viewId);
    const selectedView = savedViews.find(view => view.id === viewId);
    if (selectedView) {
      setFilters((prev) => ({ ...prev, ...selectedView.filters }));
    } else if (!viewId) {
      setFilters((prev) => ({ ...prev }));
    }
  };

  const handleClearView = async () => {
    setSelectedViewId('');
  };

  const handleExport = async (format) => {
    try {
      const response = await analyticsAPI.exportPerformance({ ...filters, format });
      const filename = `student-performance-${format}-${Date.now()}.${format}`;
      downloadBlob(response.data, filename);
      setExportAnchor(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to export analytics');
    }
  };

  const handleDigestSubscribe = async (frequency) => {
    try {
      await analyticsAPI.saveDigest({ frequency, filters });
      await loadSubscriptions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save digest');
    }
  };

  const headlineCards = useMemo(() => {
    if (!analytics) return [];
    return [
      { label: 'Average score', value: `${analytics.metrics?.averageScore || 0}%` },
      { label: 'Total attempts', value: analytics.metrics?.totalAttempts || 0 },
      { label: 'Unique learners', value: analytics.metrics?.uniqueLearners || 0 },
      { label: 'Top case', value: analytics.breakdowns?.cases?.[0]?.title || '—' }
    ];
  }, [analytics]);

  return (
    <Stack spacing={3}>
      <PerformanceFilterPanel
        filters={filters}
        onFiltersChange={handleFiltersChange}
        savedViews={savedViews}
        onCreateView={handleCreateView}
        onSelectView={handleSelectView}
        selectedViewId={selectedViewId}
        onClearView={handleClearView}
      />

      <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => loadAnalytics({ ...filters, viewId: selectedViewId || undefined })} disabled={loading}>
            Refresh analytics
          </Button>
        <Button variant="outlined" onClick={async () => {
          try {
            await analyticsAPI.refreshSnapshot(filters);
            await loadSnapshots();
          } catch (err) {
            setError(err.response?.data?.message || 'Failed to refresh snapshot');
          }
        }}>
          Capture snapshot
        </Button>
        <Button
          variant="contained"
          onClick={(event) => setExportAnchor(event.currentTarget)}
        >
          Export data
        </Button>
        <Menu
          anchorEl={exportAnchor}
          open={Boolean(exportAnchor)}
          onClose={() => setExportAnchor(null)}
        >
          <MenuItem onClick={() => handleExport('csv')}>Download CSV</MenuItem>
          <MenuItem onClick={() => handleExport('pdf')}>Download PDF</MenuItem>
        </Menu>
      </Stack>

      {error && (
        <Alert severity="error">{error}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        analytics && (
          <Stack spacing={3}>
            <Grid container spacing={2}>
              {headlineCards.map((card) => (
                <Grid item xs={12} md={3} key={card.label}>
                  <Card variant="outlined">
                    <CardHeader title={card.label} />
                    <CardContent>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {card.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardHeader title="Score distribution" />
                  <CardContent>
                    <ScoreDistributionChart distribution={analytics.metrics?.scoreDistribution} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardHeader title="Average score trend" />
                  <CardContent>
                    <TrendLineChart data={analytics.breakdowns?.timeline || []} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardHeader title="Rubric heatmap" />
                  <CardContent>
                    <RubricHeatmap metrics={analytics.metrics?.rubricHeatmap || {}} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card variant="outlined">
              <CardHeader title="Top cases" subheader="Sorted by total attempts" />
              <CardContent>
                <Stack spacing={1.5}>
                  {analytics.breakdowns?.cases?.slice(0, 8).map((item) => {
                    const provenanceChips = [];

                    if (item.originType) {
                      provenanceChips.push(
                        <Chip
                          key={`${item.caseId}-origin`}
                          label={`Origin: ${ORIGIN_LABELS[item.originType] || item.originType}`}
                          size="small"
                        />
                      );
                    }

                    if (item.authorType) {
                      provenanceChips.push(
                        <Chip
                          key={`${item.caseId}-author`}
                          label={`Author: ${AUTHOR_LABELS[item.authorType] || item.authorType}`}
                          size="small"
                        />
                      );
                    }

                    return (
                      <Stack key={item.caseId} spacing={0.75}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          justifyContent="space-between"
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          spacing={0.5}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.averageScore}% · {item.totalAttempts} attempts
                          </Typography>
                        </Stack>
                        {provenanceChips.length > 0 && (
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {provenanceChips}
                          </Stack>
                        )}
                      </Stack>
                    );
                  })}
                  {(!analytics.breakdowns?.cases || analytics.breakdowns.cases.length === 0) && (
                    <Typography variant="body2" color="text.secondary">No case activity during this window.</Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardHeader title="Digest subscriptions" />
              <CardContent>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      Subscribe to email digests using the current filters.
                    </Typography>
                    <Tooltip title="Daily digest">
                      <Button variant="outlined" onClick={() => handleDigestSubscribe('daily')}>Daily</Button>
                    </Tooltip>
                    <Tooltip title="Weekly digest">
                      <Button variant="outlined" onClick={() => handleDigestSubscribe('weekly')}>Weekly</Button>
                    </Tooltip>
                    <Tooltip title="Monthly digest">
                      <Button variant="outlined" onClick={() => handleDigestSubscribe('monthly')}>Monthly</Button>
                    </Tooltip>
                  </Stack>
                  <Divider />
                  {subscriptions.map((sub) => (
                    <Stack key={sub.id} direction={{ xs: 'column', md: 'row' }} justifyContent="space-between">
                      <Typography variant="body2">
                        {sub.frequency.charAt(0).toUpperCase() + sub.frequency.slice(1)} digest · next send {sub.nextSendAt ? dayjs(sub.nextSendAt).format('MMM D, YYYY') : 'pending'}
                      </Typography>
                      <Button size="small" onClick={async () => {
                        await analyticsAPI.deleteDigest(sub.id);
                        await loadSubscriptions();
                      }}>
                        Unsubscribe
                      </Button>
                    </Stack>
                  ))}
                  {subscriptions.length === 0 && (
                    <Typography variant="body2" color="text.secondary">No digests scheduled.</Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardHeader title="Recent snapshots" />
              <CardContent>
                <Stack spacing={1}>
                  {snapshots.map((snapshot) => (
                    <Stack key={snapshot.id} direction={{ xs: 'column', md: 'row' }} justifyContent="space-between">
                      <Typography variant="body2">Captured {dayjs(snapshot.createdAt).format('MMM D, YYYY HH:mm')}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {snapshot.metrics?.averageScore || 0}% · {snapshot.metrics?.totalAttempts || 0} attempts
                      </Typography>
                    </Stack>
                  ))}
                  {snapshots.length === 0 && (
                    <Typography variant="body2" color="text.secondary">Snapshots will appear after the first capture.</Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        )
      )}
    </Stack>
  );
};

export default StudentPerformanceDashboard;
